import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "disguiseMode";

/**
 * When "disguise mode" is enabled:
 *  - profiles with more than $500 equity are hidden
 *  - display names are rewritten ("naked" -> "Stripped", "BDSM" -> "Level Gain")
 * The toggle state is persisted in localStorage so it survives reloads.
 */
type DisguiseContextValue = {
  enabled: boolean;
  setEnabled: (value: boolean) => void;
};

const DisguiseContext = createContext<DisguiseContextValue>({
  enabled: false,
  setEnabled: () => {},
});

const readInitial = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export default function DisguiseContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [enabled, setEnabledState] = useState<boolean>(readInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(enabled));
    } catch {
      // ignore storage failures (e.g. private mode)
    }
  }, [enabled]);

  const setEnabled = useCallback((value: boolean) => {
    setEnabledState(value);
  }, []);

  const value = useMemo(() => ({ enabled, setEnabled }), [enabled, setEnabled]);

  return (
    <DisguiseContext.Provider value={value}>
      {children}
    </DisguiseContext.Provider>
  );
}

export const useDisguise = () => useContext(DisguiseContext);

/**
 * Apply the display-name rewrites. Case-insensitive so "Naked"/"naked" both
 * match; only rewrites when disguise mode is enabled.
 */
export const disguiseName = (name: string, enabled: boolean): string => {
  if (!enabled) {
    return name;
  }

  return name
    .replace(/naked/gi, "Stripped")
    .replace(/bdsm/gi, "Level Gain");
};

/**
 * Mask a wallet address to only its first 8 characters when disguise mode is
 * enabled; otherwise return it unchanged.
 */
export const disguiseAddress = (
  address: string | undefined,
  enabled: boolean,
): string => {
  if (!address) {
    return address ?? "";
  }

  return enabled ? address.slice(0, 8) : address;
};
